import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { SERVICES } from "../data/divisions";

const INQUIRY_TYPES = [
  { value: "service", label: "Requesting a Service" },
  { value: "partnership", label: "Business Partnership (any industry)" },
  { value: "subcontractor", label: "Subcontractor / Vendor / Supplier" },
  { value: "project_management", label: "Project Management Inquiry" },
  { value: "general", label: "General Inquiry / Other" },
];

// Full service directory, plus an "other" option that reveals a free-text field.
const DIVISION_OPTIONS = [
  ...SERVICES.map((s) => ({ value: s.id, label: s.name })),
  { value: "other", label: "Other — please specify" },
];

const PROJECT_STAGE = ["Planning", "Tender", "In Progress"];

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-slate mb-2">
        {label}{required ? " *" : ""}
      </span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full rounded-lg border border-stone bg-white px-4 py-3 text-navy placeholder-slate focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none transition-colors";

export default function IntakeForm({ initialType = "service" }) {
  const [type, setType] = useState(initialType);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]); // {id, filename}
  const [form, setForm] = useState({});
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function onUpload(e) {
    const chosen = Array.from(e.target.files || []);
    if (!chosen.length) return;
    setUploading(true);
    try {
      for (const f of chosen) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((cur) => [...cur, { id: data.id, filename: data.filename }]);
      }
      toast.success("File(s) uploaded");
    } catch (err) {
      toast.error("Upload failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  function validate() {
    const errs = {};
    if (!form.name) errs.name = "Required";
    if (!form.email) errs.email = "Required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Invalid email";
    if (type === "service" && !form.project_description) errs.project_description = "Required";
    if (type === "service" && form.division === "other" && !form.division_other) errs.division_other = "Please specify";
    if (type === "partnership" && !form.company) errs.company = "Required";
    if (type === "subcontractor" && !form.company) errs.company = "Required";
    if (type === "general" && !form.message) errs.message = "Required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const payload = {
        inquiry_type: type,
        ...form,
        file_ids: files.map((f) => f.id),
      };
      await api.post("/submissions", payload);
      setDone(true);
      toast.success("Submission received");
    } catch (err) {
      toast.error("Something went wrong: " + (err?.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl bg-stone p-10 md:p-14 border border-stone"
        data-testid="intake-success"
      >
        <div className="text-xs uppercase tracking-[0.25em] text-forest mb-4">Received</div>
        <h3 className="text-3xl md:text-4xl font-bold text-navy tracking-tight">Thank you.</h3>
        <p className="mt-4 text-slate max-w-lg text-lg">
          Stone Shire Group will be in touch with you soon.
        </p>
        <button
          onClick={() => { setDone(false); setForm({}); setFiles([]); }}
          className="mt-8 text-sm font-semibold text-forest hover:underline"
          data-testid="intake-reset"
        >
          Submit another inquiry →
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-8" data-testid="intake-form">
      <div>
        <span className="block text-xs uppercase tracking-widest text-slate mb-3">I am interested in *</span>
        <div className="grid sm:grid-cols-2 gap-2">
          {INQUIRY_TYPES.map((it) => (
            <button
              type="button"
              key={it.value}
              onClick={() => { setType(it.value); setErrors({}); }}
              data-testid={`intake-type-${it.value}`}
              className={`text-left rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${
                type === it.value
                  ? "border-forest bg-forest text-white"
                  : "border-stone bg-white text-navy hover:border-forest/50"
              }`}
            >
              {it.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Field label="Full name" required>
          <input data-testid="intake-name" className={inputCls} value={form.name || ""} onChange={(e)=>set("name", e.target.value)} />
          {errors.name && <span className="text-xs text-red-600 mt-1 block">{errors.name}</span>}
        </Field>
        <Field label="Email" required>
          <input data-testid="intake-email" type="email" className={inputCls} value={form.email || ""} onChange={(e)=>set("email", e.target.value)} />
          {errors.email && <span className="text-xs text-red-600 mt-1 block">{errors.email}</span>}
        </Field>

        {type !== "general" && (
          <Field label={type === "service" ? "Company (optional)" : "Company"} required={type !== "service"}>
            <input data-testid="intake-company" className={inputCls} value={form.company || ""} onChange={(e)=>set("company", e.target.value)} />
            {errors.company && <span className="text-xs text-red-600 mt-1 block">{errors.company}</span>}
          </Field>
        )}

        {type === "service" && (
          <>
            <Field label="Division / Service needed">
              <select data-testid="intake-division" className={inputCls} value={form.division || ""} onChange={(e)=>set("division", e.target.value)}>
                <option value="">Select…</option>
                {DIVISION_OPTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </Field>
            {form.division === "other" && (
              <Field label="Please specify the service" required>
                <input
                  data-testid="intake-division-other"
                  className={inputCls}
                  value={form.division_other || ""}
                  onChange={(e)=>set("division_other", e.target.value)}
                  placeholder="Describe the service you need"
                />
              </Field>
            )}
            <Field label="Timeline">
              <input data-testid="intake-timeline" className={inputCls} placeholder="e.g. Q3 2026" value={form.timeline || ""} onChange={(e)=>set("timeline", e.target.value)} />
            </Field>
            <Field label="Location">
              <input data-testid="intake-location" className={inputCls} value={form.location || ""} onChange={(e)=>set("location", e.target.value)} />
            </Field>
            <div className="md:col-span-2">
              <Field label="Project or order description" required>
                <textarea data-testid="intake-project-description" rows={4} className={inputCls} value={form.project_description || ""} onChange={(e)=>set("project_description", e.target.value)} />
                {errors.project_description && <span className="text-xs text-red-600 mt-1 block">{errors.project_description}</span>}
              </Field>
            </div>
          </>
        )}

        {type === "partnership" && (
          <>
            <Field label="Industry / Sector">
              <input data-testid="intake-industry" className={inputCls} value={form.industry || ""} onChange={(e)=>set("industry", e.target.value)} />
            </Field>
            <Field label="Type of partnership proposed">
              <input data-testid="intake-partnership-type" className={inputCls} value={form.partnership_type || ""} onChange={(e)=>set("partnership_type", e.target.value)} />
            </Field>
            <Field label="Company website or portfolio">
              <input data-testid="intake-website" className={inputCls} placeholder="https://" value={form.website || ""} onChange={(e)=>set("website", e.target.value)} />
            </Field>
            <div className="md:col-span-2">
              <Field label="Message">
                <textarea data-testid="intake-message" rows={4} className={inputCls} value={form.message || ""} onChange={(e)=>set("message", e.target.value)} />
              </Field>
            </div>
          </>
        )}

        {type === "subcontractor" && (
          <>
            <Field label="Industry / Trade">
              <input data-testid="intake-industry" className={inputCls} value={form.industry || ""} onChange={(e)=>set("industry", e.target.value)} />
            </Field>
            <Field label="Years of experience">
              <input data-testid="intake-experience" className={inputCls} value={form.years_experience || ""} onChange={(e)=>set("years_experience", e.target.value)} />
            </Field>
            <div className="md:col-span-2">
              <Field label="Certifications">
                <textarea data-testid="intake-certifications" rows={3} className={inputCls} value={form.certifications || ""} onChange={(e)=>set("certifications", e.target.value)} />
              </Field>
            </div>
            <div className="md:col-span-2">
              <span className="block text-xs uppercase tracking-widest text-slate mb-2">Documents (PDF, DOC, images)</span>
              <label className="inline-flex items-center gap-2 rounded-lg border border-stone px-4 py-3 cursor-pointer hover:border-forest transition-colors bg-white" data-testid="intake-upload-label">
                {uploading ? <Loader2 className="animate-spin" size={16}/> : <Upload size={16} />}
                <span className="text-sm">{uploading ? "Uploading…" : "Upload files"}</span>
                <input type="file" className="hidden" multiple onChange={onUpload} data-testid="intake-file-input" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.txt"/>
              </label>
              {files.length > 0 && (
                <ul className="mt-3 space-y-1">
                  {files.map((f, i) => (
                    <li key={f.id} className="flex items-center justify-between text-sm text-navy border border-stone rounded-lg px-3 py-2">
                      <span className="truncate">{f.filename}</span>
                      <button type="button" onClick={() => setFiles((cur) => cur.filter((_, idx) => idx !== i))} className="text-slate hover:text-navy" aria-label="Remove file"><X size={14}/></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}

        {type === "project_management" && (
          <>
            <Field label="Project type">
              <input data-testid="intake-project-type" className={inputCls} value={form.project_type || ""} onChange={(e)=>set("project_type", e.target.value)} />
            </Field>
            <Field label="Project size">
              <input data-testid="intake-project-size" className={inputCls} placeholder="e.g. $2M, 40,000 sqft" value={form.project_size || ""} onChange={(e)=>set("project_size", e.target.value)} />
            </Field>
            <Field label="Current stage">
              <select data-testid="intake-project-stage" className={inputCls} value={form.project_stage || ""} onChange={(e)=>set("project_stage", e.target.value)}>
                <option value="">Select…</option>
                {PROJECT_STAGE.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </>
        )}

        {type === "general" && (
          <div className="md:col-span-2">
            <Field label="Message" required>
              <textarea data-testid="intake-message" rows={5} className={inputCls} value={form.message || ""} onChange={(e)=>set("message", e.target.value)} />
              {errors.message && <span className="text-xs text-red-600 mt-1 block">{errors.message}</span>}
            </Field>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
        <button
          type="submit"
          disabled={busy}
          className="btn-primary rounded-full px-8 py-3.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
          data-testid="intake-submit"
        >
          {busy && <Loader2 className="animate-spin" size={16}/>}
          Send inquiry
        </button>
        <span className="text-xs text-slate">By submitting, you agree to be contacted by Stone Shire Group.</span>
      </div>
    </form>
  );
}
